import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { ChecklistChip } from '@components/ui/ChecklistChip/ChecklistChip';
import type { IScheduleProfession, ISchedulerTask } from '@interfaces/IScheduler';
import type { IGroupRollup } from '../../lib/rowGeometry';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import { Tooltip, TooltipContent } from '@components/ui/Tooltip/Tooltip';
import { BriefcaseIcon, CheckIcon } from '@utils/icons';
import { useDispatch } from 'react-redux';
import { useTaskLadder } from '../../hooks/useActivityLadder';
import { schedulerActions } from '@store/slices/scheduler.slice';
import { useLabelSqueeze } from '../../context/LabelSqueezeContext';
import { commitGaps } from '../../lib/commitReadiness';
import { LadderChip } from '../LadderChip/LadderChip';
import rowStyles from './RowLabels.module.css';
import styles from './RailCells.module.css';
import { SchedulerCapabilitiesContext } from '../../context/SchedulerCapabilities';

// ---------------------------------------------------------------------------
// The rail's three meta cells (2026-09-18 commit UX design, "Row rail"):
// PROFESSION · PROGRESS · STATUS. A dash in Profession is the readiness signal
// for a commit; no extra glyph. Hovering an uncommitted leaf swaps the status
// chip for the Commit button, a parent's for Commit all. Areas are not a rail
// column: the commit review asks for them and the drawer shows them.
//
// Squeezed (the panel dragged narrow — `asStatusCircle`), each cell keeps a
// form that fits: the roll-up becomes a ring, Commit an icon and
// Administrative a glyph, their full wording moved to the tooltip.
// ---------------------------------------------------------------------------

interface ICommitButtonProps {
  label: string;
  /** What the review will still ask for, shown under the label. */
  hint?: string | undefined;
  onClick: () => void;
  testId: string;
}

function CommitButton({ label, hint, onClick, testId }: ICommitButtonProps) {
  const { asStatusCircle } = useLabelSqueeze();
  // Squeezed, the tooltip carries the label the icon replaced.
  const content = asStatusCircle ? <TooltipContent title={label} lines={hint ? [hint] : []} /> : hint ? <TooltipContent title={hint} /> : null;
  return (
    <Tooltip content={content}>
      <button
        type="button"
        className={`${styles.commitBtn} ${asStatusCircle ? styles.commitIcon : ''}`}
        aria-label={asStatusCircle ? label : undefined}
        onClick={(event) => { event.stopPropagation(); onClick(); }}
        data-testid={testId}
      >
        {asStatusCircle ? <CheckIcon size={12} /> : label}
      </button>
    </Tooltip>
  );
}

function AdminLabel() {
  const { t } = useTranslation();
  const { asStatusCircle } = useLabelSqueeze();
  const label = t('scheduler.commit.kind.administrative');
  if (!asStatusCircle) return <span className={styles.admin}>{label}</span>;
  return (
    <Tooltip content={label}>
      <span className={styles.admin} role="img" aria-label={label}><BriefcaseIcon size={14} /></span>
    </Tooltip>
  );
}

interface ITaskCellsProps {
  task: ISchedulerTask;
  /** A summary task's roll-up over its leaves (the rows a Commit all fans out
   *  to); absent for a leaf. Precomputed by the layout — one pass, not one walk
   *  per row. */
  rollup?: IGroupRollup | undefined;
  /** Off where the rows are already grouped by profession (the Activities
   *  Work Plan) — the column is dropped, not left blank. */
  withProfession?: boolean;
}

export function TaskRailCells({ task, rollup, withProfession = true }: ITaskCellsProps) {
  const { t } = useTranslation();
  const { asStatusCircle } = useLabelSqueeze();
  // ONE action, dispatched directly: `useSchedulerActions` binds all ~38 action
  // creators, and this component renders once per visible row.
  const dispatch = useDispatch();
  const ladder = useTaskLadder(task);
  const isParent = rollup !== undefined;
  const open = rollup?.openLeaves ?? 0;
  // A summary row shows its subtree's mean; a leaf its own.
  const progress = rollup?.avgProgress ?? task.progressPercent;
  // Commit is `tasks.execute` — a Limited/Executive user saw the button, got
  // the whole review, and only failed on submit.
  // Read leniently: the Activities Work Plan renders these cells outside a
  // scheduler root, and its rows are all committed (no Commit to offer anyway).
  const canExecute = useContext(SchedulerCapabilitiesContext)?.canExecute ?? true;
  const commitable = canExecute && !task.isMilestone && !task.committed && task.status === 'planned';
  // What the review will still ask for — the Commit button's hint.
  const gapCount = commitable ? commitGaps(task).length : 0;
  const openCommit = () => dispatch(schedulerActions.openCommitDialog(task.id));

  return (
    <>
      {withProfession && (
        <span className={`${rowStyles.colMeta} ${styles.cell}`}>
          {task.kind === 'administrative' && !isParent ? (
            <AdminLabel />
          ) : task.profession ? (
            <ProfessionPill profession={task.profession} compact />
          ) : isParent ? '' : <span className={styles.dash}>—</span>}
        </span>
      )}
      <span className={`${rowStyles.colMeta} ${rowStyles.colPpc} ${rowStyles.ppc}`}>
        {progress}%
        {/* A promise's checks sit with its progress — the other measure of how
            far it has got — and never crowd the status chip. A squeezed rail
            has no room for them. */}
        {task.committed && !isParent && !asStatusCircle && <ChecklistChip checklist={task.checklist} className={styles.checks} />}
      </span>
      <span className={`${rowStyles.colMeta} ${styles.cell} ${styles.status} ${asStatusCircle ? styles.statusSqueezed : ''}`}>
        {isParent ? (
          <>
            {canExecute && open > 0 && (
              <CommitButton label={t('scheduler.commit.actionAllShort', { count: open })} onClick={openCommit} testId={`rail-commit-all-${task.id}`} />
            )}
            <DoneOf done={rollup.doneLeaves} total={rollup.leafCount} />
          </>
        ) : (
          <>
            {commitable && (
              <CommitButton
                label={t('scheduler.commit.actionShort')}
                hint={gapCount > 0 ? t('scheduler.commit.gapsHint', { count: gapCount }) : undefined}
                onClick={openCommit}
                testId={`rail-commit-${task.id}`}
              />
            )}
            <LadderChip rung={ladder.rung} asDot={asStatusCircle} className={styles.chip} />
          </>
        )}
      </span>
    </>
  );
}

/** A parent's "N/M done". Where the leaves' chips turn to circles, it turns
 *  into a progress ring (filled by the done share; a check once every leaf is
 *  done) so any count fits the narrowed status column. The count moves to the
 *  ring's aria-label and a marker tooltip, as the leaves' dots do. */
function DoneOf({ done, total }: { done: number; total: number }) {
  const { t } = useTranslation();
  const { asStatusCircle } = useLabelSqueeze();
  const label = t('scheduler.commit.doneOf', { done, total });
  if (!asStatusCircle) return <span className={styles.rollup}>{label}</span>;
  return (
    <Tooltip variant="marker" content={<TooltipContent title={label} />}>
      <svg className={styles.ring} viewBox="0 0 14 14" role="img" aria-label={label}>
        {done === total ? (
          <>
            <circle cx="7" cy="7" r="7" className={styles.ringDone} />
            <path d="M4 7.2l2 2 4-4.4" className={styles.ringCheck} />
          </>
        ) : (
          <>
            <circle cx="7" cy="7" r={RING_R} className={styles.ringTrack} />
            {/* pathLength=1 makes the dash the done share itself. */}
            <circle cx="7" cy="7" r={RING_R} pathLength={1} strokeDasharray={`${done / total} 1`} transform="rotate(-90 7 7)" className={styles.ringFill} />
          </>
        )}
      </svg>
    </Tooltip>
  );
}

const RING_R = 5.5;

/** A group row's roll-up: how many of its leaves are done (the Gantt), or none
 *  (the Work Plan, whose bands count its rows on the day side). */
type GroupRollup = { done: number; total: number } | { done?: undefined; total?: undefined };

/** A GROUP row's cells: its roll-up progress and profession, and its roll-up.
 *  `profession: null` drops the column (see TaskRailCells' `withProfession`). */
export function GroupRailCells({ progress, profession, ...rollup }: { progress: number; profession: IScheduleProfession | undefined | null } & GroupRollup) {
  const { asStatusCircle } = useLabelSqueeze();
  return (
    <>
      {profession !== null && (
        <span className={`${rowStyles.colMeta} ${styles.cell}`}>{profession ? <ProfessionPill profession={profession} compact /> : ''}</span>
      )}
      <span className={`${rowStyles.colMeta} ${rowStyles.colPpc} ${rowStyles.ppc}`}>{progress}%</span>
      <span className={`${rowStyles.colMeta} ${styles.cell} ${styles.status} ${asStatusCircle ? styles.statusSqueezed : ''}`}>
        {!!rollup.total && (
          <DoneOf done={rollup.done} total={rollup.total} />
        )}
      </span>
    </>
  );
}
